import React from "react";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import {
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow,
} from "@shadcn-in-nx/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import { Card, CardContent } from "@shadcn-in-nx/ui/card";
import {
    Plus,
    Loader2,
    AlertCircle,
    User,
    X,
    Search,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import UserFormDialog from "./UserFormDialog";
import UserTableRow from "./UserTableRow";
import useUsersPanelData from "./useUsersPanelData";

function UsersPanel({ workflowId, requireLogin }) {
    const { t } = useTranslation();
    const data = useUsersPanelData(workflowId);

    return (
        <div className="p-3 sm:p-4 md:p-6 pb-6 h-full flex flex-col overflow-hidden">
            <ViewAsUserBanner
                viewAsUser={data.viewAsUser}
                setViewAsUser={data.setViewAsUser}
                t={t}
            />
            <Header data={data} t={t} />
            <SearchAndFilters data={data} t={t} />
            <UserTable data={data} t={t} />
            <Pagination data={data} t={t} />
            <UserFormDialog
                open={data.isDialogOpen}
                onOpenChange={data.setIsDialogOpen}
                editingUser={data.editingUser}
                userEntitySchema={data.userEntitySchema}
                onSubmit={data.handleFormSubmit}
                formError={data.formError}
            />
        </div>
    );
}

function ViewAsUserBanner({ viewAsUser, setViewAsUser, t }) {
    if (!viewAsUser) return null;
    return (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-md px-4 py-2 mb-4 flex-shrink-0">
            <span className="text-sm text-amber-800 font-medium">
                {t("workflowUsers.viewingAs", {
                    username: viewAsUser.username,
                })}
            </span>
            <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewAsUser(null)}
                className="text-amber-700 hover:text-amber-900 h-7 px-2"
            >
                <X className="w-4 h-4 mr-1" /> {t("workflowUsers.stop")}
            </Button>
        </div>
    );
}

function Header({ data, t }) {
    return (
        <div className="flex items-center justify-between gap-2 mb-3 flex-shrink-0">
            <h2 className="text-lg font-bold text-gray-900 truncate">
                {t("workflowUsers.title")}{" "}
                {data.pagination?.total != null
                    ? `(${data.pagination.total})`
                    : ""}
            </h2>
            <Button
                onClick={data.handleCreateUser}
                size="sm"
                className="flex items-center gap-1.5 shrink-0"
            >
                <Plus className="w-4 h-4" /> {t("workflowUsers.addUser")}
            </Button>
        </div>
    );
}

function SearchAndFilters({ data, t }) {
    return (
        <div className="flex flex-col gap-2 mb-3 flex-shrink-0">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                    type="text"
                    placeholder={t("workflowUsers.searchPlaceholder")}
                    value={data.searchQuery}
                    onChange={data.handleSearchChange}
                    className="pl-10"
                />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <Select
                    value={data.statusFilter}
                    onValueChange={data.handleStatusFilterChange}
                >
                    <SelectTrigger className="w-auto min-w-[110px] h-8 text-xs">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {t("workflowUsers.allStatuses")}
                        </SelectItem>
                        <SelectItem value="active">
                            {t("workflowUsers.active")}
                        </SelectItem>
                        <SelectItem value="blocked">
                            {t("workflowUsers.userIsBlocked")}
                        </SelectItem>
                    </SelectContent>
                </Select>
                <Select
                    value={data.roleFilter}
                    onValueChange={data.handleRoleFilterChange}
                >
                    <SelectTrigger className="w-auto min-w-[100px] h-8 text-xs">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {t("workflowUsers.allRoles")}
                        </SelectItem>
                        <SelectItem value="user">
                            {t("workflowUsers.roleUser")}
                        </SelectItem>
                        <SelectItem value="admin">
                            {t("workflowUsers.roleAdmin")}
                        </SelectItem>
                    </SelectContent>
                </Select>
                {data.pagination && (
                    <span className="text-xs text-gray-400 ml-auto">
                        {data.pagination.total > 0
                            ? `${(data.currentPage - 1) * 20 + 1}-${Math.min(
                                  data.currentPage * 20,
                                  data.pagination.total
                              )} / ${data.pagination.total}`
                            : "0"}
                    </span>
                )}
            </div>
        </div>
    );
}

function UserTable({ data, t }) {
    if (data.loading) {
        return (
            <Card className="flex-1">
                <CardContent className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </CardContent>
            </Card>
        );
    }
    if (data.error) {
        return (
            <Card className="flex-1">
                <CardContent className="p-6">
                    <div className="flex items-center gap-2 p-4 text-sm text-red-600 bg-red-50 rounded-md">
                        <AlertCircle className="w-4 h-4" /> {data.error}
                    </div>
                </CardContent>
            </Card>
        );
    }
    if (data.users.length === 0) {
        return (
            <Card className="flex-1">
                <CardContent className="text-center py-8 text-gray-500 p-6">
                    <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium mb-2">
                        {t("workflowUsers.noUsersFound")}
                    </h3>
                    <p className="text-sm mb-4">
                        {t("workflowUsers.noUsersDescription")}
                    </p>
                </CardContent>
            </Card>
        );
    }
    return (
        <Card className="flex-1 flex flex-col overflow-hidden">
            <CardContent className="flex-1 overflow-hidden p-0">
                <div className="h-full overflow-auto p-2 sm:p-4 lg:p-6">
                    <Table className="min-w-full">
                        <TableHeader>
                            <TableRow>
                                <TableHead>
                                    {t("workflowUsers.username")}
                                </TableHead>
                                <TableHead className="hidden sm:table-cell">
                                    {t("workflowUsers.email")}
                                </TableHead>
                                <TableHead className="hidden lg:table-cell">
                                    {t("workflowUsers.name")}
                                </TableHead>
                                <TableHead>{t("workflowUsers.role")}</TableHead>
                                <TableHead className="hidden sm:table-cell">
                                    {t("workflowUsers.status")}
                                </TableHead>
                                <TableHead className="hidden lg:table-cell">
                                    {t("workflowUsers.created")}
                                </TableHead>
                                {data.configKeys.map((key) => (
                                    <TableHead
                                        key={key}
                                        className="hidden xl:table-cell"
                                    >
                                        {data.getFieldConfig(key).label}
                                    </TableHead>
                                ))}
                                <TableHead className="text-right">
                                    {t("workflowUsers.actions")}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data.users.map((user) => (
                                <UserTableRow
                                    key={user.id}
                                    user={user}
                                    configKeys={data.configKeys}
                                    getFieldConfig={data.getFieldConfig}
                                    viewAsUserId={data.viewAsUser?.id}
                                    onEdit={data.handleEditUser}
                                    onDelete={data.handleDeleteUser}
                                    onViewAs={data.handleViewAsUser}
                                    onToggleBlock={data.handleToggleBlock}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
}

function Pagination({ data, t }) {
    if (data.totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between gap-2 mt-3 flex-shrink-0">
            <span className="text-xs text-gray-500">
                {data.currentPage} / {data.totalPages}
            </span>
            <div className="flex items-center gap-1">
                <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => data.setCurrentPage((p) => p - 1)}
                    disabled={data.currentPage === 1}
                >
                    <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                {Array.from(
                    { length: Math.min(3, data.totalPages) },
                    (_, i) => {
                        let pageNum;
                        if (data.totalPages <= 3) pageNum = i + 1;
                        else if (data.currentPage <= 2) pageNum = i + 1;
                        else if (data.currentPage >= data.totalPages - 1)
                            pageNum = data.totalPages - 2 + i;
                        else pageNum = data.currentPage - 1 + i;
                        return (
                            <Button
                                key={pageNum}
                                variant={
                                    data.currentPage === pageNum
                                        ? "default"
                                        : "outline"
                                }
                                size="icon"
                                onClick={() => data.setCurrentPage(pageNum)}
                                className="h-7 w-7 text-xs"
                            >
                                {pageNum}
                            </Button>
                        );
                    }
                )}
                <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => data.setCurrentPage((p) => p + 1)}
                    disabled={data.currentPage === data.totalPages}
                >
                    <ChevronRight className="w-3.5 h-3.5" />
                </Button>
            </div>
        </div>
    );
}

export default UsersPanel;
